import { Map as ImmutableMap } from 'immutable';
import { getColumnBasicType, getColumnFormat, getColumnLabel, isMagicMeasure } from 'reporting-snowflake/relational/utils/column-utils';
import { getEncodingList } from 'reporting-snowflake/relational/utils/visual-utils';
import { DATASET_TABLE } from 'reporting-snowflake/relational/schema/table-records';
// @ts-expect-error untyped dependency,
import { toMagicMeasureProperty } from 'reporting-snowflake/relational/resolve/hydrate';
import { Formats } from 'reporting-snowflake/relational/schema/column-records';
import { link } from 'reporting-snowflake/relational/resolve/link';
import { resort } from 'reporting-snowflake/relational/resolve/resort';
// @ts-expect-error untyped dependency
import { shouldShowId } from 'reporting-snowflake/relational/metadata/references/external/external-resolvers';
// @ts-expect-error untyped dependency
import { getExternalResolver } from 'reporting-snowflake/relational/metadata/references/external/external-resolvers';
// @ts-expect-error untyped dependency
import { getObjectResolver } from 'reporting-snowflake/relational/metadata/references/object/object-resolvers';
import { GLOBAL_NULL } from 'reporting-data/constants/defaultNullValues';
import { addGoalData, hasGoalData } from 'reporting-snowflake/relational/resolve/goal';
import { FORMATTERS } from 'reporting-snowflake/relational/resolve/format';
import { isFieldExpression } from 'reporting-snowflake/dataset/expression-utils';
import { Expression } from 'reporting-snowflake/dataset/expression-records';
function formatNumericPropertyReference(key, raasResponse, property) {
  return property.get('references').map((reference, referenceKey) => {
    if (referenceKey === GLOBAL_NULL) {
      return reference;
    }
    const propertyLabel = raasResponse.getIn(['identifiers', key, referenceKey, 'references', 'label']);
    if (!propertyLabel) {
      // TODO: Empty space before the (.  Will fix this separately in SF and here.
      return reference.set('label', ` (${referenceKey})`);
    }
    return reference.set('label', `${propertyLabel} (${referenceKey})`);
  });
}
function applyReferenceFormatters(property, column, columnFormat) {
  const field = column.field;
  if (!field || isFieldExpression(field)) {
    return property;
  }
  const referenceFormatter = FORMATTERS[columnFormat];
  if (referenceFormatter) {
    const references = property.get('references');
    const formatter = referenceFormatter(column);
    const updatedReferences = references.map(value => {
      const label = value.get('id');
      if (!label || label === GLOBAL_NULL) {
        return value;
      }
      return value.set('label', formatter(label));
    });
    return property.set('references', updatedReferences);
  }
  return property;
}
export const applyRaaSDatasetFixes = ({
  reportDefinition,
  metaData,
  dataset,
  datasetBuildOptions,
  raasResponse,
  allJoinMetadata
}) => {
  const updatedProperties = dataset.properties.map((property, key) => {
    const column = reportDefinition.getIn(['columns', key]);

    /**
     * dataset may contain properties that are not in the original reportDefinition (auxilary/generated query columns)
     */
    if (!column) {
      return property;
    }
    const {
      tables,
      properties,
      datasetDefinition
    } = metaData;
    const field = column.field;
    const {
      table: tableName,
      name: fieldName
    } = field;
    if (!tableName && !reportDefinition.expressions) {
      return property;
    }
    const snowflakeTable = tables.get(tableName);
    let snowflakeProperty = isMagicMeasure(column) ? toMagicMeasureProperty(field) : properties.getIn([tableName, fieldName]);
    const maybeDatasetField = tableName === DATASET_TABLE && datasetDefinition ? datasetDefinition.fields.get(fieldName) : undefined;
    if (maybeDatasetField) {
      const parsedExpression = Expression(maybeDatasetField === null || maybeDatasetField === void 0 ? void 0 : maybeDatasetField.expression);
      const {
        table,
        name
      } = parsedExpression;
      snowflakeProperty = properties.getIn([table, name]);
    }

    /**
     * Fix 1: Column Label (RaaS vs FE resolve)
     * 1) Casing issue - "Count of Contacts" vs "Count of contacts"
     * 2) Aggregation in label - "First response time" vs "Average (First response time)"
     */
    const updatedLabel = getColumnLabel(column, snowflakeProperty, snowflakeTable, maybeDatasetField, reportDefinition.expressions, reportDefinition.table, allJoinMetadata);

    /**
     * Fix 2: Column type
     * FE resolve doesn't account for enumeration type and defaults to string
     *
     * The dataSet and isMeasure check only for validation purposes due to the following logic.
     * In case the role of column is measure, the default dataset property is returned w/o properly setting the type.  The
     * default type is set using getColumnFormat with undefined snowflake property that returns a basic type.
     * Context: https://git.hubteam.com/HubSpot/reporting/blob/0afdd2212f343dee6f3e508e4c86e336e51c6b4f/reporting-snowflake/static-1.49972/js/relational/resolve/hydrate.js#L231
     */

    const isDatasetFieldMeasure = maybeDatasetField && datasetBuildOptions.isMeasure(key);
    const missingSnowflakeProperty = !snowflakeProperty;
    const shouldGetColumnBasicType = isDatasetFieldMeasure && missingSnowflakeProperty;
    const type = shouldGetColumnBasicType ? getColumnBasicType(column) : getColumnFormat(column, snowflakeProperty);

    /**
     * Fix 3: RaaS dataset is missing numberDisplayHint
     */
    const numberDisplayHint = snowflakeProperty && snowflakeProperty.numberDisplayHint;

    // Batch the updates
    return property.withMutations(context => {
      let updatedProperty = context.set('label', updatedLabel).set('type', type).set('numberDisplayHint', numberDisplayHint);

      /**
       * Fix 4: Migrate companyCurrencyCode property from property.format.companyCurrencyCode to
       * property.currencyCode.
       */
      const companyCurrencyCode = property.getIn(['format', 'companyCurrencyCode']);
      if (companyCurrencyCode) {
        updatedProperty = updatedProperty.set('companyCurrencyCode', companyCurrencyCode);

        // If the column's options.format.type is set to 'auto' but the field is a currency field
        // and has a companyCurrencyCode, we should set the currencyCode to the companyCurrencyCode
        // and let the visualization handle the formatting. Otherwise, the label will not be formatted correctly.
        // sample: https://app.hubspotqa.com/reporting-sandbox/882894063/report?linkId=e536a3109b3b45979a69816b98923683
        updatedProperty.setIn(['format', 'currencyCode'], companyCurrencyCode);
      }

      /**
       * Fix 5: SF visualization relies on default propertyFormatters to hydrate references for
       * Measures that do not have a special formatter defined.
       * context: https://git.hubteam.com/HubSpot/reporting/blob/b3a77b0f2e83ec3cf22aad9ea6160d6331c469e1/reporting-snowflake/static-1.49972/js/relational/resolve/format.ts#L123
       */

      const hasCustomFormatting = () => {
        /**
         * In some cases, FE resolve relies on visualization formatters to format data.
         * RaaS resolve buildDataset step builds references with partial
         * formatting information that result in visualization formatters not being applied.
         * This change removes RaaS generated references in case a column has a custom formatter.
         *
         * TODO: SF resolve sets their own formatting object in visualization that the propertyFormatter
         * in visualization uses while rendering viz. (https://git.hubteam.com/HubSpot/reporting/blob/e49b463a55e41c8d08b48fd83b01db4f590d9026/reporting-visualizations/static-1.53428/js/formatters/propertyFormatter.js#L161)
         * Investigate why it was implemented as such instead of updating the references.
         *
         * Context: https://hubspot.slack.com/archives/CPLH0B5B7/p1697827611776089
         */

        const encodings = reportDefinition.getIn(['visual', 'encodings']);
        const encodingList = encodings && getEncodingList(encodings);
        const vizColumn = encodingList && encodingList.find(col => col.get('column') === key);
        const formatType = vizColumn && vizColumn.getIn(['options', 'format', 'type']);
        return formatType && formatType !== 'auto';
      };

      // if formats = currency, we should let the visualization handle the formatting.
      // regardless whether it is a 'measure' or not.
      const shouldRemoveReferences = datasetBuildOptions.isMeasure(key) && ![Formats.DATETIME, Formats.DATE, Formats.HTML, Formats.DURATION].includes(property.get('type')) || property.get('type') === Formats.CURRENCY;
      if (shouldRemoveReferences || hasCustomFormatting()) {
        updatedProperty = updatedProperty.set('references', ImmutableMap({}));
      }

      /**
       * Fix 9: SF FE resolve formats the data post process (https://git.hubteam.com/HubSpot/reporting/blob/e40abd10c1eb1e2a8c231b64abdc3d4552d928d8/reporting-snowflake/static-1.49972/js/relational/resolve/process.ts#L45)
       * Migrating of the changes here for validation
       */
      updatedProperty = applyReferenceFormatters(updatedProperty, column, type);

      /**
       * RaaS response has options under the format property whereas the SF has it as it as a top
       * level property.  Update the property to RaaS dataset property to match SF.
       */
      const propertyOptions = updatedProperty.getIn(['format', 'options']);
      if (propertyOptions) {
        const formattedOptions = propertyOptions.map((optionValue, optionsName) => optionValue.set('value', optionsName)).toList().sort((a, b) => a.get('displayOrder') - b.get('displayOrder'));
        updatedProperty = updatedProperty.set('options', formattedOptions);
      }
      if (!snowflakeProperty) {
        return updatedProperty;
      }

      /*
       * Backend label sorting relies on canBeResolvedWithLabelExpression flag on the
       * Snowflake property. This flag doesn't exist on RaaS Resolve path. Adding an
       * override for this property.
       */
      if (snowflakeProperty.has('canBeResolvedWithLabelExpression')) {
        updatedProperty = updatedProperty.set('canBeResolvedWithLabelExpression', snowflakeProperty.get('canBeResolvedWithLabelExpression'));
      }
      if (maybeDatasetField) {
        return updatedProperty;
      }
      const getShowRecordIds = () => reportDefinition.visual && reportDefinition.visual.options.get('showRecordIds', true);
      /**
       * Fix 6: SF has special formatting for numeric references and email_champaign (https://git.hubteam.com/HubSpot/reporting/blob/f10a0552768d2533d972b55c3a243f320d0594b4/reporting-snowflake/static-1.49972/js/relational/metadata/references/external/external-resolvers.js#L81)
       * For example, in case of recordId column is formatted as "companyName (objectId)".  e.g. 'hubspot (4101832)' and 'WeathermanCo (4302342)'
       */
      if (getShowRecordIds() && snowflakeProperty.options && snowflakeProperty.options.isEmpty() && shouldShowId(snowflakeProperty) && (
      // Checks if this property is resolved via external-resolver or object-resolver
      getExternalResolver(snowflakeTable, snowflakeProperty) || getObjectResolver(snowflakeTable, snowflakeProperty))) {
        updatedProperty = updatedProperty.set('references', formatNumericPropertyReference(key, raasResponse, updatedProperty));
      }
      return updatedProperty;
    });
  }).toMap();

  /**
   * Fix 7: SF resolve creates/inserts objects links into the dataset that is used to create links in the table.
   * RaaS has different structure to links in the response.  Following the FE approach for now.
   */
  const datasetWithLinks = link(reportDefinition, metaData, dataset);
  let updatedDataset = datasetWithLinks.set('properties', updatedProperties);

  /**
   * Fix 10: Resort Dataset
   * SF resolve applies a FE resorting of data: https://git.hubteam.com/HubSpot/reporting/blob/e40abd10c1eb1e2a8c231b64abdc3d4552d928d8/reporting-snowflake/static-1.49972/js/relational/resolve/process.ts#L30
   */
  updatedDataset = resort(reportDefinition, metaData, updatedDataset);

  /**
   * Fix 8: SF resolve adds Goals data on FE during post processing. Following that approach here as well
   */
  if (hasGoalData(reportDefinition)) {
    updatedDataset = addGoalData(reportDefinition, metaData, updatedDataset);
  }
  return updatedDataset;
};
export const __TESTABLE__ = {
  formatNumericPropertyReference,
  applyReferenceFormatters
};